import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpHeaders,
} from '@angular/common/http';
import { catchError, switchMap, mergeMap } from 'rxjs/operators';
import { from, Observable, throwError } from 'rxjs';
import { Auth } from 'aws-amplify';
import { environment } from '@environments/environment';
import { CognitoUserSession, CognitoUser } from 'amazon-cognito-identity-js';
import { HEADERS_INTERCEPTOR_ALLOWED_URLS } from '@app/constants';

@Injectable()
export class HeadersInterceptor implements HttpInterceptor {
  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return from(Auth.currentSession()).pipe(
      mergeMap(cognitoUserSession => {
        const idTokenExpire = cognitoUserSession.getIdToken().getExpiration();
        const refreshToken = cognitoUserSession.getRefreshToken();
        const currentTimeSeconds = Math.round(+new Date() / 1000);

        const getHeaders = (
          tokenId: string,
          headers: Record<string, string> = {}
        ) => {
          return new HttpHeaders({
            Authorization: `Bearer ${tokenId}`,
            [environment.assessmentKey]: environment.assessmentValue,
            ...headers,
          });
        };

        if (idTokenExpire < currentTimeSeconds) {
          return from<Promise<CognitoUser>>(
            Auth.currentAuthenticatedUser()
          ).pipe(
            switchMap(cognitoUser => {
              return from(
                new Promise<CognitoUserSession>((resolve, reject) => {
                  cognitoUser.refreshSession(
                    refreshToken,
                    (err, cognitoUserSession) => {
                      return err ? reject(err) : resolve(cognitoUserSession);
                    }
                  );
                })
              ).pipe(
                switchMap(cognitoUserSession => {
                  const headers = getHeaders(
                    cognitoUserSession.getIdToken().getJwtToken()
                  );
                  const cloneRequest = request.clone({ headers });
                  return next.handle(cloneRequest);
                }),
                catchError((err: string) => {
                  console.error('HeadersInterceptor: error refreshing token', err);
                  return from(Auth.signOut()).pipe(
                    switchMap(() => throwError(() => new Error(err)))
                  );
                })
              );
            })
          );
        }

        const allowedUrls = Object.values(HEADERS_INTERCEPTOR_ALLOWED_URLS);
        const isAllowedUrl = allowedUrls.some(url => request.url.includes(url));
        
        if (!isAllowedUrl) {
          const headers = getHeaders(
            cognitoUserSession.getIdToken().getJwtToken()
          );
          const cloneRequest = request.clone({ headers });
          return next.handle(cloneRequest);
        }

        return from(Auth.currentSession()).pipe(
          mergeMap(() => {
            const tokenId = cognitoUserSession.getIdToken().getJwtToken();
            const headers = getHeaders(tokenId);
            const cloneRequest = request.clone({ headers });
            return next.handle(cloneRequest);
          })
        );
      }),
      catchError((err) => {
        console.error('HeadersInterceptor: error getting cognito session', err);
        // Para localhost, permitir la petición sin token de Cognito,
        // pero SIEMPRE incluyendo el x-api-key-dev que el backend exige
        if (request.url.includes('localhost')) {
          console.log('HeadersInterceptor: localhost detected, adding API key without auth token');
          const headers = new HttpHeaders({
            [environment.assessmentKey]: environment.assessmentValue,
          });
          const cloneRequest = request.clone({ headers });
          return next.handle(cloneRequest);
        }
        return throwError(() => err);
      })
    );
  }
}
